import { api, ApiTags } from '../../api';
import type { PromptDto } from '../../../components/types';
import { promptsActions } from '../../slices/prompts';

import {
    PromptRequest,
    PromptResponse,
    MyPromptsResponse,
    MyPromptsParams,
    UserPromptsRequest,
    UserPromptsResponse,
} from './types';
import { transformPromptDtoToModel } from './mappers';

const promptApi = api.injectEndpoints({
    endpoints: build => ({
        // Получить промпт по id
        getPrompt: build.query<PromptResponse, PromptRequest>({
            query: ({ promptId }) => `/prompts/${promptId}`,
            transformResponse: transformPromptDtoToModel,
            providesTags: (result, _, request) => (result ? [{ type: ApiTags.Prompts, id: request.promptId }] : []),
            onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    // Добавляем промпт в EntityAdapter кеш
                    dispatch(promptsActions.addPrompts([data]));
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
        }),

        // Получить похожие промпты по id промпта
        getSimilarPrompts: build.query<PromptDto[], PromptRequest>({
            query: ({ promptId }) => ({ url: '/prompts', params: { hash: promptId } }),
            transformResponse: (response: { prompts: PromptDto[] }) => response.prompts.map(transformPromptDtoToModel),
            serializeQueryArgs: ({ endpointName }) => endpointName,
            providesTags: result => {
                if (!result) {
                    return [{ type: ApiTags.Prompts, id: 'LIST' }];
                }

                return [
                    ...result.map(({ id }) => ({ type: ApiTags.Prompts, id })),
                    // { type: ApiTags.Prompts, id: 'LIST' },
                ];
            },
            onQueryStarted: async ({ promptId }, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    // Сохраняем PromptDto напрямую в EntityAdapter
                    dispatch(promptsActions.addPrompts(data));
                    dispatch(promptsActions.setSimilarPrompts({ promptId, prompts: data }));
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
        }),

        // Получить 3 популярных промпта
        getPopularPrompts: build.query<PromptDto[], void>({
            query: () => '/prompts/popular',
            transformResponse: (response: PromptDto[]) => response.map(transformPromptDtoToModel),
            providesTags: [{ type: ApiTags.Prompts, id: 'POPULAR' }],
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    // Сохраняем PromptDto напрямую в EntityAdapter
                    dispatch(promptsActions.addPrompts(data));
                    dispatch(promptsActions.setPopularPrompts(data));
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
        }),

        // Получить все промпты текущего пользователя с пагинацией
        getMyPrompts: build.query<MyPromptsResponse, MyPromptsParams | void>({
            query: params => ({
                url: '/prompts/user/my',
                params: params || {},
            }),
            serializeQueryArgs: ({ endpointName }) => endpointName,
            transformResponse: (response: MyPromptsResponse) => ({
                prompts: response.prompts.map(transformPromptDtoToModel),
                next_cursor: response.next_cursor,
                has_more_prompts: response.has_more_prompts,
            }),
            onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    const { cursor } = params || {};

                    if (!cursor) {
                        dispatch(
                            promptsActions.setMyFeedCache({
                                cache: {
                                    promptIds: data.prompts.map(p => p.id),
                                    nextCursor: data.next_cursor,
                                    hasMore: data.has_more_prompts,
                                },
                                prompts: data.prompts,
                            }),
                        );
                    } else {
                        dispatch(
                            promptsActions.appendToMyFeedCache({
                                promptIds: data.prompts.map(p => p.id),
                                nextCursor: data.next_cursor,
                                hasMore: data.has_more_prompts,
                                prompts: data.prompts,
                            }),
                        );
                    }
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
            providesTags: [{ type: ApiTags.Prompts, id: 'MY' }],
        }),

        // Получить промпты пользователя по login или publicId с пагинацией
        getUserPrompts: build.query<UserPromptsResponse, UserPromptsRequest>({
            query: ({ loginOrPublicId, cursor, limit }) => ({
                url: `/prompts/user/${loginOrPublicId}`,
                params: {
                    ...(cursor && { cursor }),
                    ...(limit && { limit }),
                },
            }),
            serializeQueryArgs: ({ queryArgs }) => queryArgs.loginOrPublicId,
            transformResponse: (response: UserPromptsResponse) => ({
                prompts: response.prompts.map(transformPromptDtoToModel),
                next_cursor: response.next_cursor,
                has_more_prompts: response.has_more_prompts,
            }),
            onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    const { loginOrPublicId, cursor } = params;

                    if (!cursor) {
                        dispatch(
                            promptsActions.setUserFeedCache({
                                loginOrPublicId,
                                cache: {
                                    promptIds: data.prompts.map(p => p.id),
                                    nextCursor: data.next_cursor,
                                    hasMore: data.has_more_prompts,
                                },
                                prompts: data.prompts,
                            }),
                        );
                    } else {
                        dispatch(
                            promptsActions.appendToUserFeedCache({
                                loginOrPublicId,
                                promptIds: data.prompts.map(p => p.id),
                                nextCursor: data.next_cursor,
                                hasMore: data.has_more_prompts,
                                prompts: data.prompts,
                            }),
                        );
                    }
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
            providesTags: (result, error, arg) => [
                { type: ApiTags.Prompts, id: `USER_PROMPTS_${arg.loginOrPublicId}` },
            ],
        }),

        markAsViewed: build.mutation<void, number[]>({
            query: promptIds => ({
                url: '/prompts/viewed',
                method: 'PUT',
                body: promptIds,
            }),
        }),
    }),
});

export const {
    useGetPromptQuery,
    useGetSimilarPromptsQuery,
    useGetUserPromptsQuery,
    useGetMyPromptsQuery,
    useGetPopularPromptsQuery,
    useMarkAsViewedMutation,
} = promptApi;

export { promptApi };
